import { useWorkspaces } from "@/api/workspaces";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/utils";
import { route } from "@/utils/route";
import type { MetadataIcon, Workspace } from "@cohesive/api-client";
import { Icon as MetadataIconRenderer } from "@cohesive/ui-components";
import Fuse from "fuse.js";
import { Check, ChevronDown, Home as HomeIcon, Layers, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const displayName = (ws: Workspace) =>
  ws.is_private ? "My workspace" : ws.name;

/** The flipping caret hint for switcher triggers (needs `group` on the trigger). */
export function SwitcherCaret({ className }: { className?: string }) {
  return (
    <ChevronDown
      aria-hidden
      className={cn(
        "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-150 group-hover:text-foreground group-data-[state=open]:rotate-180",
        className,
      )}
    />
  );
}

/** A workspace's icon at title scale, with the shared Layers fallback. */
function WorkspaceRowIcon({ icon }: { icon?: MetadataIcon | null }) {
  return (
    <span className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center">
      {icon ? (
        <MetadataIconRenderer icon={icon} size={18} />
      ) : (
        <Layers className="h-[18px] w-[18px] text-n-70" />
      )}
    </span>
  );
}

interface WorkspaceSwitcherPopoverProps {
  /** Workspace page being viewed; undefined when the surface is Home (`/`). */
  currentWorkspaceId?: string;
  /**
   * Whether the pinned Home entry is offered. On for the page-title triggers
   * (they answer "where am I?"); off for the composer scope chip, which is
   * about which workspace a chat lands in — Home isn't a chat target.
   */
  includeHome?: boolean;
  /**
   * The trigger markup. Must contain a `PopoverTrigger` (usually
   * `asChild` around a `group`-classed button so `SwitcherCaret` and the
   * open-state background react to it).
   */
  children: React.ReactNode;
}

/**
 * The switcher list itself: Home (the cross-workspace overview) pinned first,
 * then every workspace — "My workspace" leading, rest alphabetical — behind a
 * Fuse search. Patterned after the composer's `@` connectors menu (Popover +
 * Command, same chrome), with title-sized rows to match the headers it opens
 * from. Selecting navigates: Home → `/`, a workspace → its detail page.
 *
 * Shared by every entry point — the workspace title, the home greeting, and
 * the composer's scope chip — so "switch the active workspace" means the same
 * thing everywhere.
 */
export function WorkspaceSwitcherPopover({
  currentWorkspaceId,
  includeHome = true,
  children,
}: WorkspaceSwitcherPopoverProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");

  // Cached query; the rail / home browser already fetched it.
  const { data: workspaces } = useWorkspaces();
  const sorted = useMemo(() => {
    const list = [...(workspaces ?? [])];
    // "My workspace" first, then alphabetical — matches the rail's ordering.
    return list.sort((a, b) =>
      a.is_private !== b.is_private
        ? Number(b.is_private) - Number(a.is_private)
        : displayName(a).localeCompare(displayName(b)),
    );
  }, [workspaces]);

  const search = useMemo(
    () =>
      new Fuse(sorted, {
        keys: [{ name: "label", getFn: displayName }],
        threshold: 0.2,
      }),
    [sorted],
  );
  const filtered = useMemo(() => {
    if (filter === "") return sorted;
    return search.search(filter).map((result) => result.item);
  }, [search, filter, sorted]);

  /** Navigate to a workspace, or to Home when `ws` is omitted. */
  const go = (ws?: Workspace) => {
    setOpen(false);
    setFilter("");
    if (!ws) {
      if (currentWorkspaceId) navigate(ROUTES.HOME);
      return;
    }
    if (ws.id !== currentWorkspaceId) {
      navigate(route(ROUTES.WORKSPACES.DETAIL, { workspaceId: ws.id }));
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setFilter("");
      }}
    >
      {children}
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[384px] min-w-[384px] rounded-[14px] border p-2"
      >
        <Command
          className="w-full overflow-hidden rounded-none bg-transparent"
          shouldFilter={false}
        >
          <div className="relative px-2 pb-2 [&_.border-b]:border-0">
            <CommandInput
              placeholder="Search workspaces..."
              value={filter}
              onValueChange={setFilter}
              className="h-11"
            />
            {filter && (
              <button
                onClick={() => setFilter("")}
                className="absolute right-5 top-1/2 -translate-y-1/2 p-1"
              >
                <X className="h-4 w-4 text-muted-foreground transition-colors hover:text-foreground" />
              </button>
            )}
          </div>
          <CommandList className="scrollbar-thin scrollbar-track-transparent scrollbar-thumb-neutral-300 dark:scrollbar-thumb-neutral-600 max-h-[400px] overflow-y-auto">
            {/* Home hides while filtering — the search is over workspaces,
                same as the @-menu hiding "Add file" once a filter is typed. */}
            {includeHome && !filter && (
              <CommandGroup>
                <CommandItem
                  value="__home__"
                  className="flex h-12 items-center gap-3 px-4"
                  onSelect={() => go()}
                >
                  <span className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center">
                    <HomeIcon className="h-[18px] w-[18px] text-n-70" />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-base font-medium">Home</span>
                    <span className="text-xs text-muted-foreground">
                      All workspaces
                    </span>
                  </span>
                  {!currentWorkspaceId && (
                    <Check className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                </CommandItem>
              </CommandGroup>
            )}
            {filtered.length === 0 && (
              <p className="p-4 text-center text-sm text-muted-foreground">
                No workspaces found.
              </p>
            )}
            {filtered.length > 0 && (
              <CommandGroup heading="Workspaces">
                {filtered.map((ws) => (
                  <CommandItem
                    key={ws.id}
                    value={ws.id}
                    className="flex h-12 items-center gap-3 px-4"
                    onSelect={() => go(ws)}
                  >
                    <WorkspaceRowIcon icon={ws.metadata?.icon} />
                    {/* Title-sized to match the header this opens from. */}
                    <span className="min-w-0 flex-1 truncate text-base font-medium">
                      {displayName(ws)}
                    </span>
                    {ws.id === currentWorkspaceId && (
                      <Check className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface WorkspaceSwitcherProps {
  /** The workspace whose page is being viewed. */
  currentWorkspaceId?: string;
  workspaceName: string;
  icon?: MetadataIcon | null;
}

/**
 * The workspace title above the composer, doubling as the switcher trigger:
 * the workspace's icon + name at title scale with a flipping caret hint.
 */
export function WorkspaceSwitcher({
  currentWorkspaceId,
  workspaceName,
  icon,
}: WorkspaceSwitcherProps) {
  return (
    <WorkspaceSwitcherPopover currentWorkspaceId={currentWorkspaceId}>
      {/* h1 around the button (a button can't contain a heading) keeps the
          page-title semantics the plain header had. */}
      <h1 className="min-w-0">
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Switch workspace"
            className="group -ml-1 flex min-w-0 items-center gap-2.5 rounded-lg py-0.5 pl-1 pr-1.5 hover:bg-muted/60 data-[state=open]:bg-muted/60"
          >
            <WorkspaceRowIcon icon={icon} />
            <span className="truncate text-xl font-medium text-n-90">
              {workspaceName}
            </span>
            <SwitcherCaret />
          </button>
        </PopoverTrigger>
      </h1>
    </WorkspaceSwitcherPopover>
  );
}
